import { useEffect, useState, useRef, useContext} from "react";
import { marked } from "marked";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import DOMPurify from "dompurify";
import { useTranslationContext } from "../contexts/TranslationContext";
import { ThemeContext } from "../contexts/ThemeContext";
import { env } from "../environment";
import RelatedFiles from "./RelatedFiles/RelatedFiles";
import { downloadFile } from "../utility/downloadFile";
import downloadBlueIcon from "../assets/images/downloadBlueIcon.svg";
import { toast } from "react-toastify";

function preserveMathBlocks(text) {
  const mathBlocks = [];
  const replaced = text.replace(/(\$\$.*?\$\$|\\\[.*?\\\]|\\\(.*?\\\)|\$.*?\$)/gs, (match) => {
    const placeholder = `@@MATH${mathBlocks.length}@@`;
    mathBlocks.push(match);
    return placeholder;
  });
  return { replaced, mathBlocks };
}

function restoreMathBlocks(html, mathBlocks) {
  return mathBlocks.reduce(
    (acc, math, index) => acc.replace(`@@MATH${index}@@`, math),
    html
  );
}

function MessageComponent({ message, handleCitationClick }) {
  const [mounted, setMounted] = useState(false);
  const mathJaxRef = useRef(null);
  const {isDarkMode} = useContext(ThemeContext);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted && mathJaxRef.current && window.MathJax?.typesetPromise) {
      window.MathJax.typesetPromise().catch((err) =>
        console.error("MathJax typeset error:", err)
      );
    }
  }, [mounted, message.text]);
   const { translations } = useTranslationContext();
  const loadingText = translations["loading"] || "Loading";
  const downloadText = translations["download_text"] || "Download";
  const citationText = translations["citation_text"] || "Citations";

  const validCitations = message.citations
    ?.filter((citation) => citation?.title && citation?.url)
    .map((citation) => {
      const fullURL = `${env.baseURL}/proxy/file?url=${(citation.url)}`;
      return {
        ...citation,
        url: fullURL,
      };
    });

    const handleDownload = async (e, citation) => {
      e.stopPropagation();
      try {
        await downloadFile({ url: citation.url, filename: citation.title });
      } catch {
       toast.error("Download failed. Please try again.");
      }
    };

  const renderAssistantMessage = () => {
    if (message.text === '__loading__') {
      return (
        <div className={`loading-dots ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
          <span>{loadingText}</span>
        </div>
      );
    }
    const citationHtml =
      validCitations?.length > 0
        ? `
        <div style="margin-top: 1.5em;">
          <strong>${citationText}:</strong>
          <div class="citation-list">
            ${validCitations
              .map(
                (citation, index) => `
                <div class="citation-item">
                  <button class="citation-link button-style" type="button" data-index="${index}">
                    ${citation.title}
                    <button
                      type="button"
                      title=${downloadText}
                      class="citation-download-btn button-style"
                      data-index="${index}"
                    >
                      <img src=${downloadBlueIcon} alt="download-icon"/>
                    </button>
                  </button>
                </div>`
              )
              .join("")}
          </div>
        </div>`
        : "";

    const { replaced, mathBlocks } = preserveMathBlocks(message.text);
    const htmlMessage = marked(replaced);
    const sanitizedMessage = DOMPurify.sanitize(
      restoreMathBlocks(htmlMessage, mathBlocks),
      {
        ADD_TAGS: ["math", "mi", "mo", "mn", "ms", "mrow"],
        ADD_ATTR: ["xmlns"],
      }
    );
    const finalHtml = sanitizedMessage + citationHtml;

    return (
      mounted && (
        <MathJaxContext
          version={3}
          config={{
            loader: { load: ["[tex]/ams"] },
            tex: {
              packages: { "[+]": ["ams"] },
              inlineMath: [["\\(", "\\)"], ["$", "$"]],
              displayMath: [["\\[", "\\]"], ["$$", "$$"]],
            },
          }}
        >
          <MathJax>
            <div
              ref={mathJaxRef}
              dangerouslySetInnerHTML={{ __html: finalHtml }}
              onClick={(e) => {
                const target = e.target;
                if (!target) return;
                const downloadBtn = target.closest(".citation-download-btn");
                const citationLink = target.closest(".citation-link");

                if (citationLink && !downloadBtn) {
                  e.preventDefault();
                  const citationIndex = parseInt(citationLink.getAttribute("data-index"), 10);
                  if (!isNaN(citationIndex) && validCitations?.[citationIndex]) {
                    handleCitationClick(validCitations[citationIndex], citationIndex);
                  }
                }
                if (downloadBtn) {
                  e.preventDefault();
                  e.stopPropagation(); // stops link click
                  const citationIndex = parseInt(downloadBtn.getAttribute("data-index"), 10);
                  if (!isNaN(citationIndex) && validCitations?.[citationIndex]) {
                    handleDownload(e, validCitations[citationIndex]);
                  }
                }
              }}
            />
          </MathJax>
        </MathJaxContext>
      )
    );
  };

  const renderDisclaimer = () => {
    if (!message.disclaimerText) return null;

    const disclaimerPoints = message.disclaimerText.split(". ");
    const firstPoint = disclaimerPoints.shift();

    const disclaimerHtml = `
      <br/>
      <span class="red-text">${firstPoint}.</span>
      ${
        disclaimerPoints.length > 0
          ? `<ul class="disclaimer-list">
              ${disclaimerPoints
                .map((point) => (point.trim() ? `<li>${point.trim()}</li>` : ""))
                .join("")}
            </ul>`
          : ""
      }
    `;

    return (
      <div
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(disclaimerHtml),
        }}
      />
    );
  };

  return (
    <div className={`message-text ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      {message.sender === "user" ? (
        <span>{message.text}</span>
      ) : (
        renderAssistantMessage()
      )}
     <RelatedFiles links={message.relevantLinks} />
      {/* {message.sender === "assistant" &&
        (message.totalTokens !== undefined || message.responseTime !== undefined) && (
          <div className={`response-info ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
            <p>
              {message.totalTokens !== undefined &&
                `${message.totalTokens} Tokens (P: ${message.promptTokens} + R: ${message.completionTokens}) `}
              {message.responseTime !== undefined && `[${message.responseTime} ms]`}
            </p>
            <hr />
          </div>
        )} */}
      {renderDisclaimer()}
    </div>
  );
}

export default MessageComponent;